const React = require('react');
const { createElement: h } = React;
const NodeIcon = require('./NodeIcon');
const {
  ConsoleLogOutput,
  FilePreviewDisplay,
  AgentSummary
} = require('./WorkflowNode');
const NodeParamsForm = require('./NodeParamsForm');
const SetVariableSidebarEditor = require('./SetVariableSidebarEditor');
const VariableSidebarEditor = require('./VariableSidebarEditor');
const ConditionSidebarEditor = require('./ConditionSidebarEditor');
const NodeCredentialsSection = require('./NodeCredentialsSection');
const BrowserRunRecorderEditor = require('./BrowserRunRecorderEditor');
const BrowserActionEditor = require('./BrowserActionEditor');
const BrowserAgenticActionEditor = require('./BrowserAgenticActionEditor');
const ApifyEditor = require('./ApifyEditor');
const { HyperParamChips } = require('./WorkflowNode');
const { getTileTheme } = require('../lib/tileTheme');
const { compactSetVariableTitle, compactConditionTitle } = require('../lib/canvasUtils');

// Sleep + Loop are simple enough to render right here as labeled-row forms,
// matching the styling used by the larger sidebar editors.
function SleepEditor(props) {
  var paramValues = props.paramValues || {};
  var onParamChange = props.onParamChange || function() {};
  var duration = paramValues.duration !== undefined ? paramValues.duration : 1000;
  var unit = String(paramValues.unit || 'ms');
  return h('div', { className: 'wf-params-form' },
    h('div', { className: 'wf-params-row' },
      h('div', { className: 'wf-params-row-label' }, 'Duration'),
      h('input', {
        className: 'wf-params-input',
        type: 'number',
        min: 0,
        value: duration,
        onChange: function(e) { onParamChange('duration', e.target.value); }
      })
    ),
    h('div', { className: 'wf-params-row' },
      h('div', { className: 'wf-params-row-label' }, 'Unit'),
      h('select', {
        className: 'wf-params-input',
        value: unit,
        onChange: function(e) { onParamChange('unit', e.target.value); }
      },
        h('option', { value: 'ms' }, 'Milliseconds'),
        h('option', { value: 's' }, 'Seconds'),
        h('option', { value: 'm' }, 'Minutes')
      )
    )
  );
}

function LoopEditor(props) {
  var paramValues = props.paramValues || {};
  var onParamChange = props.onParamChange || function() {};
  var mode = String(paramValues.mode || 'each item');
  return h('div', { className: 'wf-params-form' },
    h('div', { className: 'wf-params-row' },
      h('div', { className: 'wf-params-row-label' }, 'Mode'),
      h('input', {
        className: 'wf-params-input',
        type: 'text',
        value: mode,
        onChange: function(e) { onParamChange('mode', e.target.value); }
      })
    )
  );
}

// Resolve which editor to render for a given icon. Receives `{ paramValues,
// onParamChange, variableNodes, runtimeOutput }` and returns React children.
// Returning null means "no dedicated sidebar editor — fall through to the
// generic NodeParamsForm (hyperparameters list) or empty-state."
//
// Note: `icon === 'agent'` is overloaded — it can mean either the legacy
// system agent node (category="agent") OR a planner-built custom node with
// an AI persona (category="custom", has its own generationPrompt + hp). The
// legacy AgentSummary only fits the former; the latter must fall through to
// the normal hyperparameters form so users can edit Creativity/Response
// Length/etc. The category check is performed by the caller, not here.
function resolveEditor(icon) {
  switch (icon) {
    case 'set-variable':  return function(p) { return h(SetVariableSidebarEditor, p); };
    case 'variables':     return function(p) { return h(VariableSidebarEditor, p); };
    case 'console-log':   return function(p) { return h(ConsoleLogOutput, p); };
    case 'file-preview':  return function(p) { return h(FilePreviewDisplay, { runtimeOutput: p.runtimeOutput }); };
    case 'agent':         return function(p) { return h(AgentSummary, { paramValues: p.paramValues }); };
    case 'sleep':         return function(p) { return h(SleepEditor, p); };
    case 'loop':          return function(p) { return h(LoopEditor, p); };
    case 'browser-run':   return function(p) { return h(BrowserRunRecorderEditor, p); };
    case 'browser-action': return function(p) { return h(BrowserActionEditor, p); };
    case 'browser-agentic-action': return function(p) { return h(BrowserAgenticActionEditor, p); };
    case 'apify':         return function(p) { return h(ApifyEditor, p); };
    default:              return null;
  }
}

// Older saved nodes were created before the `'multiline'` param type existed,
// so their long-text fields are still stored as `type: 'string'`. We re-tag
// known long-text keys at render time so the sidebar shows a textarea for
// them without a one-time data migration. New nodes created from the current
// templates already have `type: 'multiline'` and skip this branch.
var LEGACY_MULTILINE_KEYS = {
  'classifier:instruction': { rows: 4 },
  'computer-use:prompt':    { rows: 6 }
};

// Helper text re-applied at render time for nodes saved before a param gained
// its `hint`. Keyed by `<icon>:<key>`; new nodes already carry the hint and
// keep their own. Lets existing canvases self-heal without a data migration.
var PARAM_HINTS = {
  'tool-agent:prompt': 'If set, this overrides any input coming from the connection. Leave empty to use the connected input as the task.'
};

// Build an icon-aware mapper. The map is keyed by `<icon>:<key>` so identical
// keys on different node types don't collide.
function makeMultilineUpgrader(icon) {
  return function(param) {
    if (!param || !param.key) return param;
    var hintText = param.hint || PARAM_HINTS[icon + ':' + param.key] || null;
    var withHint = hintText ? Object.assign({}, param, { hint: hintText }) : param;
    // Already explicitly multiline — leave the type alone (but keep the hint).
    if (String(withHint.type || '').toLowerCase() === 'multiline') return withHint;
    if (withHint.multiline === true) return withHint;
    var ml = LEGACY_MULTILINE_KEYS[icon + ':' + withHint.key];
    if (!ml) return withHint;
    return Object.assign({}, withHint, { type: 'multiline', rows: withHint.rows || ml.rows });
  };
}

// Right-sidebar panel that shows the selected node's parameters as a proper
// labeled form (matching the Agent editor's visual style), not a cramped
// chip strip.
function NodeParamsPanel(props) {
  var node = props.node;
  var variableNodes = props.variableNodes || [];
  var runtimeOutput = props.runtimeOutput;
  // Live run status for this node — forwarded to NodeParamsForm so number
  // params written as N++ / N-- show their resolved counter value at runtime.
  var nodeStatus = props.nodeStatus || null;
  var nodeCallCount = props.nodeCallCount || 0;
  var onParamChange = props.onParamChange || function() {};
  var onParamRename = props.onParamRename || function() {};
  var onParamAdd = props.onParamAdd || null;
  var onParamRemove = props.onParamRemove || null;
  var onConditionRuleChange = props.onConditionRuleChange || function() {};
  var onCredentialChange = props.onCredentialChange || function() {};
  var onClose = props.onClose || function() {};
  // Live (read-only) mode: parameters/hyperparameters are not editable here —
  // they can only be changed in Staging. We render a small notice instead.
  var readOnly = !!props.readOnly;
  // Live "credentials only": show ONLY the per-node account picker (editable),
  // and drop the params editor entirely. Used in Live mode where account
  // selection is the single editable, savable node config (like variable values).
  var credentialsOnly = !!props.credentialsOnly;

  if (!node) return null;

  var icon = node.icon;
  // Mirror the canvas card's dynamic title for set-variable / condition nodes
  // so the sidebar header reflects the node's current target/rule (e.g.
  // "Set Intent") instead of the static type label ("Set Variable").
  var dynamicTitle = icon === 'set-variable'
    ? compactSetVariableTitle(node)
    : icon === 'condition'
      ? compactConditionTitle(node)
      : '';
  var title = (dynamicTitle && dynamicTitle.trim()) || node.title || 'Node';
  // For condition / set-variable, the dynamic title already conveys the rule
  // ("@Google = ?" / "Set Intent"), so the static type-label subtitle ("Route
  // by yes/no rule" / "Update a variable at runtime") becomes redundant noise
  // — drop it. Other node types keep their template subtitle.
  var subtitle = (icon === 'condition' || icon === 'set-variable') ? '' : (node.subtitle || '');
  var hyperparameters = Array.isArray(node.hyperparameters) ? node.hyperparameters : [];
  var paramValues = node.paramValues || {};
  var theme = getTileTheme(icon);

  function changeParam(key, value) {
    onParamChange(node.id, key, value);
  }
  function renameParam(oldKey, newLabel) {
    onParamRename(node.id, oldKey, newLabel);
  }
  function addParam(draft) {
    if (onParamAdd) onParamAdd(node.id, draft);
  }
  function removeParam(key) {
    if (onParamRemove) onParamRemove(node.id, key);
  }

  // Editor selection: dedicated sidebar editor first, then generic
  // NodeParamsForm for hyperparameter-driven nodes, then empty-state.
  // Custom-category nodes that happen to use icon="agent" (planner-built AI
  // personas) carry their own hyperparameters and must fall through to the
  // generic form — the legacy AgentSummary only fits system agent nodes.
  var editorRenderer = resolveEditor(icon);
  if (icon === 'agent' && node.category === 'custom') {
    editorRenderer = null;
  }
  var editorBody;
  if (icon === 'condition') {
    // Condition gets the full rule builder. The LHS variable comes from the
    // node's `variable` hyperparameter; we render the existing chip picker
    // for it so users keep variable autocomplete inside the rich rule editor.
    var variableHyperparam = hyperparameters.find(function(p) { return p && p.key === 'variable'; });
    editorBody = h(ConditionSidebarEditor, {
      conditionRule: node.conditionRule,
      paramValues: paramValues,
      variableNodes: variableNodes,
      onConditionRuleChange: function(nextRule) { onConditionRuleChange(node.id, nextRule); },
      onParamChange: changeParam,
      renderVariableChip: variableHyperparam
        ? function() {
            return h(HyperParamChips, {
              params: [variableHyperparam],
              paramValues: paramValues,
              onParamChange: changeParam,
              variableNodes: variableNodes
            });
          }
        : null
    });
  } else if (editorRenderer) {
    editorBody = editorRenderer({
      nodeId: node.id,
      paramValues: paramValues,
      onParamChange: changeParam,
      variableNodes: variableNodes,
      browserActionNodes: props.browserActionNodes || [],
      runtimeOutput: runtimeOutput
    });
  } else if (icon === 'router') {
    // Router's `labels` hyperparameter is edited inline on the card tile
    // (one row per route). Showing it again in the sidebar would duplicate
    // the editor and confuse users, so suppress the generic form here.
    editorBody = h('div', { className: 'wf-params-empty' }, 'No editable parameters for this node.');
  } else if (icon === 'classifier') {
    // Classifier edits `labels` inline on the card (one row per label), so
    // hide it from the sidebar; `instruction` falls through to the form and
    // renders as a textarea via its `type: 'multiline'`.
    var classifierParams = hyperparameters.filter(function(p) { return p && p.key !== 'labels'; });
    editorBody = classifierParams.length > 0
      ? h(NodeParamsForm, {
          params: classifierParams.map(makeMultilineUpgrader(icon)),
          paramValues: paramValues,
          onParamChange: changeParam,
          onParamRename: renameParam,
          variableNodes: variableNodes,
          nodeStatus: nodeStatus,
          nodeCallCount: nodeCallCount
        })
      : h('div', { className: 'wf-params-empty' }, 'No editable parameters for this node.');
  } else if (hyperparameters.length > 0) {
    // Pure custom nodes (user-built code) and the Start node (workflow inputs)
    // allow rename / add / clear. AI custom (icon='agent' + category='custom')
    // has system-defined params like Temperature / Max Tokens — value-edit
    // only, no rename/add.
    var canMutateSchema = icon === 'custom' || icon === 'start';
    // Deleting a param row is allowed on all three configurable types —
    // custom, Start, and AI custom (agent) — per product decision, even though
    // AI-custom params are system-defined (deleting one may change how it runs).
    var canDeleteParam = canMutateSchema || (icon === 'agent' && node.category === 'custom');
    // Tool Agent edits its model tier via the click-to-cycle badge on the node
    // tile (like the AI custom node), so hide `aiTier` from the sidebar form —
    // only its Prompt/instruction is edited here.
    var visibleParams = icon === 'tool-agent'
      ? hyperparameters.filter(function(p) { return p && p.key !== 'aiTier'; })
      : hyperparameters;
    editorBody = h(NodeParamsForm, {
      params: visibleParams.map(makeMultilineUpgrader(icon)),
      paramValues: paramValues,
      onParamChange: changeParam,
      onParamRename: canMutateSchema ? renameParam : null,
      onParamAdd: (canMutateSchema && onParamAdd) ? addParam : null,
      onParamRemove: (canDeleteParam && onParamRemove) ? removeParam : null,
      variableNodes: variableNodes,
      nodeStatus: nodeStatus,
      nodeCallCount: nodeCallCount
    });
  } else if (onParamAdd && (icon === 'custom' || icon === 'start')) {
    // No params yet, but a custom node or the Start node — show only the
    // inline "+ Add param" affordance so users can grow the form from empty.
    editorBody = h(NodeParamsForm, {
      params: [],
      paramValues: paramValues,
      onParamChange: changeParam,
      onParamRename: renameParam,
      onParamAdd: addParam,
      variableNodes: variableNodes
    });
  } else {
    editorBody = h('div', { className: 'wf-params-empty' }, 'No editable parameters for this node.');
  }

  // In read-only (Live) mode, suppress every editor and show a notice. The whole
  // body is replaced so no input/select/textarea is ever rendered.
  if (readOnly) {
    editorBody = h('div', { className: 'wf-params-empty' },
      'Parameters are read-only in the live version. Switch to Staging to edit them.'
    );
  }

  return h('div', { className: 'wf-params-panel' },
    h('div', { className: 'wf-params-header' },
      h('div', { className: 'wf-params-icon wf-node-tile-color-' + theme.family },
        // Provider nodes (e.g. Apify) carry a logo image — show it here so the
        // panel header matches the canvas tile; fall back to the SVG glyph.
        node.logoUrl
          ? h('img', {
              src: node.logoUrl,
              alt: '',
              style: { width: 22, height: 22, objectFit: 'contain', borderRadius: 4 },
              onError: function(e) { e.target.style.display = 'none'; }
            })
          : h(NodeIcon, { type: icon, size: 22 })
      ),
      h('div', { className: 'wf-params-titlebox' },
        h('div', { className: 'wf-params-title', title: title }, title),
        subtitle ? h('div', { className: 'wf-params-subtitle', title: subtitle }, subtitle) : null
      ),
      h('button', {
        type: 'button',
        className: 'wf-params-close',
        onClick: onClose,
        title: 'Close (deselect node)'
      },
        h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
          h('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
          h('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
        )
      )
    ),
    h('div', { className: 'wf-params-body' },
      // Per-node OAuth account picker. Self-hides when the node's code doesn't
      // reference any <provider>.login() call, so nodes that don't touch OAuth
      // keep their original empty-state look. It stays EDITABLE in both Staging
      // and Live: in Staging it sits above the params editor; in Live
      // (credentialsOnly) it's the only thing shown. The plain read-only Live
      // path (no credentialsOnly) still hides it.
      (readOnly && !credentialsOnly) ? null : h(NodeCredentialsSection, {
        node: node,
        onCredentialChange: onCredentialChange
      }),
      // In credentialsOnly mode the params editor is suppressed entirely — the
      // account picker above is the whole panel.
      credentialsOnly
        ? (NodeCredentialsSection.detectProviders(node).length > 0
            ? null
            : h('div', { className: 'wf-params-empty' },
                'This node has no connected account to choose.'))
        : editorBody
    )
  );
}

module.exports = NodeParamsPanel;
